import React from "react";

import { InfoMessage, Select } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useQuery } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";
import ProjectsService from "@/services/ProjectsService";

import { toWorkspaceOptions } from "@/utils/UWorkspaces";

import { TCompany, TProject, TViewSelectionItem } from "@/types";

interface IViewSelectionFieldsProps {
	projects: TViewSelectionItem[];
	clients: TViewSelectionItem[];
	onChange: (field: string, value: unknown) => void;
}

const toItems = (
	ids: number[],
	rows: { id: number; name: string; color?: string | null }[]
): TViewSelectionItem[] =>
	ids
		.map((id) => rows.find((row) => row.id === id))
		.filter((row): row is { id: number; name: string; color?: string } =>
			Boolean(row)
		)
		.map((row) => ({
			id: row.id,
			name: row.name,
			color: row.color ?? "#000000",
		}));

/**
 * What the view gathers. Picking nothing is a view of everything the
 * person reaches, which is what a fresh account starts with — so an
 * empty selection is a choice, not a blank field.
 */
export const ViewSelectionFields: React.FC<IViewSelectionFieldsProps> = ({
	projects,
	clients,
	onChange,
}) => {
	const projectsQuery = useQuery({
		queryKey: ["projects", "selectable"],
		queryFn: (): Promise<TProject[]> => ProjectsService.list(),
	});
	const clientsQuery = useQuery({
		queryKey: ["clients", "selectable"],
		queryFn: (): Promise<TCompany[]> =>
			CompaniesService.fetchAll("clients"),
	});

	const projectRows = projectsQuery.data ?? [];
	const clientRows = clientsQuery.data ?? [];

	return (
		<div className="flex flex-col gap-4">
			<Select<number>
				multiple
				label={t("views.form.projects")}
				options={toWorkspaceOptions(projectRows, (project) => ({
					value: project.id,
					label: project.name,
				}))}
				value={projects.map((project) => project.id)}
				onChange={(value) =>
					onChange(
						"projects",
						toItems(
							Array.isArray(value) ? (value as number[]) : [],
							projectRows
						)
					)
				}
				placeholder={t("views.form.projectsPlaceholder")}
			/>
			<Select<number>
				multiple
				label={t("views.form.clients")}
				options={toWorkspaceOptions(clientRows, (client) => ({
					value: client.id,
					label: client.name,
				}))}
				value={clients.map((client) => client.id)}
				onChange={(value) =>
					onChange(
						"clients",
						toItems(
							Array.isArray(value) ? (value as number[]) : [],
							clientRows
						)
					)
				}
				placeholder={t("views.form.clientsPlaceholder")}
			/>
			{0 === projects.length && 0 === clients.length && (
				<InfoMessage color="info">
					{t("views.form.everything")}
				</InfoMessage>
			)}
		</div>
	);
};
